import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import brandsmartLogo from '../../Images/BrandsMartUSA_Logo.png';
import homeiqLogoWhite from '../../Images/HomeIQ-White-Logo.png';
import homeiqLogo from '../../Images/HomeIQ-General-logo.png';
import { useBranding } from '../../utils/branding';

interface AuthLayoutProps {
  children: ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const branding = useBranding();
  
  const logoSrc = branding.theme.id === '301io' ? homeiqLogo : brandsmartLogo;
  const is301io = branding.theme.id === '301io';
  const leftCopy = is301io
    ? {
        tagline: branding.tagline ?? 'Digital Transformation Experts',
        headline1: 'HomeIQ,',
        headline2: 'intelligently',
        headline3: 'orchestrated.',
        description:
          'A premium home platform by 301io that unifies protection, service, and AI-first insights in one experience.',
        features: [
          'Connected asset intelligence',
          'Proactive maintenance signals',
          'AI-guided diagnosis',
          'Energy & cost clarity',
        ],
      }
    : {
        tagline: t('layout.tagline'),
        headline1: t('layout.headline1'),
        headline2: t('layout.headline2'),
        headline3: t('layout.headline3'),
        description: t('layout.description'),
        features: [
          t('layout.features.assets'),
          t('layout.features.maintenance'),
          t('layout.features.ai'),
          t('layout.features.energy'),
        ],
      };
  const leftPanelStyle = is301io
    ? {
        backgroundImage: [
          'linear-gradient(165deg, rgba(6,6,10,0.97) 0%, rgba(12,9,13,0.95) 40%, rgba(76,10,38,0.55) 78%, rgba(159,18,74,0.28) 100%)',
          'radial-gradient(ellipse at 20% 10%, rgba(190,24,93,0.16), transparent 55%)',
          "url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80')",
        ].join(', '),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        backgroundImage: [
          'linear-gradient(to bottom right, rgba(0,89,179,0.95) 0%, rgba(0,70,140,0.90) 45%, rgba(220,20,60,0.15) 100%)',
          "url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80')",
        ].join(', '),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
  
  return (
    <div className="min-h-screen flex bg-white dark:bg-zinc-950">
      {/* ── Left Panel ──────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[52%] relative overflow-hidden flex-col"
        style={leftPanelStyle}
      >
        {/* Subtle grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full px-12 pt-12 pb-12">
          {/* Logo */}
          <div className="flex items-start animate-fade-in-down stagger-1 -ml-9">
            <img
              src={is301io ? homeiqLogoWhite : brandsmartLogo}
              alt={`${branding.brandName} Logo`}
              className={`w-auto object-contain ${is301io ? 'h-24' : 'h-9'} ${is301io ? '' : 'brightness-0 invert'}`}
            />
          </div>

          {/* Main headline */}
          <div className="mt-auto mb-auto space-y-6 max-w-sm">
            <div className="space-y-4">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-rose-200/60 animate-fade-in-up stagger-2">
                {leftCopy.tagline}
              </p>
              <h1 className="text-4xl xl:text-5xl font-light text-white leading-[1.2] tracking-tight animate-fade-in-up stagger-3">
                {leftCopy.headline1}<br />
                <span className="font-semibold text-white drop-shadow-[0_0_20px_rgba(190,24,93,0.3)]">{leftCopy.headline2}</span>
                <br />{leftCopy.headline3}
              </h1>
              <p className="text-white/70 text-sm leading-relaxed animate-fade-in-up stagger-4">
                {leftCopy.description}
              </p>
            </div>

            {/* Feature list */}
            <div className="space-y-3 pt-2">
              {leftCopy.features.map((label, i) => (
                <div key={label} className={`flex items-center gap-3 animate-fade-in-up stagger-${i + 5}`}>
                  <div className="h-4 w-4 rounded-full border border-rose-400/30 bg-rose-500/5 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 10 10" className="h-2 w-2 text-rose-300/80" fill="currentColor">
                      <path d="M8.5 2.5L4 8 1.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </div>
                  <span className="text-xs text-white/70">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-8 border-t border-white/15 animate-fade-in stagger-8">
            <p className="text-xs text-white/50">© 2026 {branding.brandName}</p>
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-rose-400/80 animate-pulse shadow-sm shadow-rose-400/30" />
              <span className="text-xs text-white/60">{t('layout.trusted')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ─────────────────────────────────── */}
      {/* overflow-y-auto + min-h-full trick keeps the form pinned — prevents jump on focus/blur */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-zinc-950">
        <div className="min-h-full flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px] animate-fade-in-up stagger-2">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-start mb-10 group">
            <img 
              src={logoSrc} 
              alt={`${branding.brandName} Logo`} 
              className={`w-auto object-contain ${is301io ? 'h-14' : 'h-8'} transition-all duration-500 ease-out group-hover:scale-110`}
            />
          </div>

          {children}
        </div>
        </div>
      </div>
    </div>
  );
}

